/**
 * RegulatoryConstellationGraph — Cytoscape.js concentric-layout visualization
 * for ANY node as center with all connections radiating outward.
 *
 * Supports two modes:
 *
 * 1. TENANT-CENTRIC (ConstellationData):
 *    Ring 0: Tenant → Ring 1-3: Regulatory nodes by jurisdiction
 *
 * 2. UNIVERSAL (UniversalConstellationData):
 *    Ring 0: Any entity (actor, person, etc.)
 *    Ring 1: Organizations (tenants the entity connects to)
 *    Ring 2-4: Regulatory nodes (via organizations), deduplicated
 *    Shared regulatory nodes glow — connected to multiple orgs
 *
 * Example: Sarah Johnson (center)
 *   ├── ASSIGNED_TO → ABC Home Health → [HIPAA, OSHA, CMS, ...]
 *   ├── LOCATED_AT  → Sunrise Senior Living → [HIPAA, MN-245D, ...]
 *   └── PARTICIPATES → Bright Smiles Dental → [HIPAA, ADA, ...]
 *
 * Click any node → re-center on it (onRecenter callback).
 *
 * DRR-075: Universal constellation visualization.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import cytoscape, { type Core, type NodeSingular } from 'cytoscape';
import {
  Badge,
  Box,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';

import type {
  ConstellationData,
  Jurisdiction,
  RegulatoryNode,
} from './hooks/useRegulatoryConstellation';
import type {
  UniversalConstellationData,
} from './hooks/useUniversalConstellation';
import { RegulatoryNodeDetailPanel } from './RegulatoryNodeDetailPanel';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Concentric ring values — higher = closer to center. */
const RING_VALUES: Record<string, number> = {
  center: 100,
  tenant: 100,       // tenant-centric mode
  organization: 80,  // universal mode: connected orgs
  federal: 55,
  state: 35,
  payer: 15,
  standard: 15,
};

/** Node background colors by role/jurisdiction. */
const NODE_COLORS: Record<string, string> = {
  center: '#7048e8',       // Violet — focal entity
  tenant: '#228be6',       // FERP blue — tenant-centric center
  organization: '#228be6', // Blue — org connections
  federal: '#e03131',      // Red — highest authority
  state: '#f08c00',        // Amber — state-level
  payer: '#2f9e44',        // Green — contractual
  standard: '#868e96',     // Gray — voluntary
};

/** Node border colors (darker shade). */
const NODE_BORDER_COLORS: Record<string, string> = {
  center: '#5f3dc4',
  tenant: '#1864ab',
  organization: '#1864ab',
  federal: '#c92a2a',
  state: '#e67700',
  payer: '#2b8a3e',
  standard: '#495057',
};

/** Edge colors by relationship type (center → org edges). */
const ORG_EDGE_COLORS: Record<string, string> = {
  ASSIGNED_TO: '#40c057',  // Green — employment
  LOCATED_AT: '#fab005',   // Yellow — residence
  PARTICIPATES: '#7950f2', // Purple — participation
  CONTAINS: '#868e96',     // Gray — containment
  CORRELATES: '#f06595',   // Pink — correlation
};

/** Edge colors by jurisdiction (org → regulatory edges). */
const REG_EDGE_COLORS: Record<string, string> = {
  federal: '#e03131',
  state: '#f08c00',
  payer: '#2f9e44',
  standard: '#adb5bd',
};

/** Legend entries. */
const RING_LEGEND: Array<{ key: string; label: string; color: string }> = [
  { key: 'organization', label: 'Organization', color: '#228be6' },
  { key: 'federal', label: 'Federal', color: '#e03131' },
  { key: 'state', label: 'State', color: '#f08c00' },
  { key: 'payer', label: 'Payer', color: '#2f9e44' },
  { key: 'standard', label: 'Standard', color: '#868e96' },
];

const EDGE_LEGEND: Array<{ key: string; label: string; color: string; style: string }> = [
  { key: 'ASSIGNED_TO', label: 'Works at', color: '#40c057', style: 'solid' },
  { key: 'LOCATED_AT', label: 'Lives at', color: '#fab005', style: 'solid' },
  { key: 'PARTICIPATES', label: 'Patient at', color: '#7950f2', style: 'solid' },
  { key: 'REGULATED_BY', label: 'Mandatory', color: '#e03131', style: 'solid' },
  { key: 'GOVERNED_BY', label: 'Voluntary', color: '#adb5bd', style: 'dashed' },
];

// ---------------------------------------------------------------------------
// Cytoscape Stylesheet
// ---------------------------------------------------------------------------

const CONSTELLATION_STYLES = [
  // Base node
  {
    selector: 'node',
    style: {
      'background-color': '#228be6',
      'border-width': 2,
      'border-color': '#1864ab',
      label: 'data(label)',
      'text-valign': 'center',
      'text-halign': 'center',
      'font-size': 10,
      'font-weight': 'bold',
      color: '#fff',
      width: 65,
      height: 50,
      'text-wrap': 'wrap',
      'text-max-width': '80px',
    },
  },
  // Center node — largest, violet
  {
    selector: 'node[role="center"]',
    style: {
      width: 120,
      height: 75,
      'font-size': 13,
      'border-width': 5,
      'text-max-width': '110px',
      shape: 'round-rectangle',
    },
  },
  // Tenant center (single-mode) — large blue
  {
    selector: 'node[jurisdiction="tenant"]',
    style: {
      width: 110,
      height: 70,
      'font-size': 12,
      'border-width': 4,
      'text-max-width': '100px',
      shape: 'round-rectangle',
    },
  },
  // Organization nodes — medium, round-rectangle
  {
    selector: 'node[role="organization"]',
    style: {
      width: 95,
      height: 60,
      'font-size': 11,
      'border-width': 3,
      'text-max-width': '95px',
      shape: 'round-rectangle',
    },
  },
  // Federal nodes — diamond
  {
    selector: 'node[jurisdiction="federal"]',
    style: { shape: 'diamond', width: 70, height: 60 },
  },
  // State nodes — hexagon
  {
    selector: 'node[jurisdiction="state"]',
    style: { shape: 'hexagon', width: 70, height: 60 },
  },
  // Payer nodes — ellipse
  {
    selector: 'node[jurisdiction="payer"]',
    style: { shape: 'ellipse', width: 70, height: 55 },
  },
  // Standard nodes — dashed border
  {
    selector: 'node[jurisdiction="standard"]',
    style: {
      shape: 'round-rectangle',
      width: 65,
      height: 50,
      'border-style': 'dashed',
    },
  },
  // Shared regulatory nodes (connected via 2+ orgs) — glow effect
  {
    selector: 'node[?is_shared]',
    style: {
      'border-width': 4,
      'border-color': '#ffd43b',
      'shadow-blur': 8,
      'shadow-color': '#ffd43b',
      'shadow-opacity': 0.6,
      'shadow-offset-x': 0,
      'shadow-offset-y': 0,
    },
  },
  // Primary regulatory bodies
  {
    selector: 'node[?is_primary]',
    style: { 'border-width': 4, width: 80, height: 65 },
  },
  // Conditional nodes
  {
    selector: 'node[?is_conditional]',
    style: { 'border-style': 'dotted', opacity: 0.85 },
  },
  // Selection highlight
  {
    selector: 'node:selected',
    style: {
      'border-width': 5,
      'border-color': '#f03e3e',
      'overlay-opacity': 0.15,
      'overlay-color': '#f03e3e',
    },
  },
  // Base edge
  {
    selector: 'edge',
    style: {
      width: 2,
      'line-color': '#adb5bd',
      'target-arrow-color': '#adb5bd',
      'target-arrow-shape': 'triangle',
      'curve-style': 'bezier',
      'arrow-scale': 1,
    },
  },
  // Center → Organization edges — thicker, colored by type
  {
    selector: 'edge[layer="org"]',
    style: { width: 3.5 },
  },
  // Org → Regulatory edges — thinner
  {
    selector: 'edge[layer="regulatory"]',
    style: { width: 2 },
  },
  // REGULATED_BY edges — solid
  {
    selector: 'edge[edge_type="REGULATED_BY"]',
    style: { 'line-style': 'solid' },
  },
  // GOVERNED_BY edges — dashed
  {
    selector: 'edge[edge_type="GOVERNED_BY"]',
    style: {
      'line-style': 'dashed',
      'line-color': '#adb5bd',
      'target-arrow-color': '#adb5bd',
    },
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Get concentric ring value for a node based on its role and jurisdiction. */
function getRingValue(role: string, jurisdiction?: string): number {
  if (role === 'center') return RING_VALUES.center ?? 100;
  if (role === 'organization') return RING_VALUES.organization ?? 80;
  return RING_VALUES[jurisdiction || 'federal'] || 15;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface RegulatoryConstellationGraphProps {
  /** Single-tenant constellation (tenant-centric mode). */
  readonly constellation?: ConstellationData | null;
  /** Universal constellation (any node as center, multi-hop). */
  readonly universalConstellation?: UniversalConstellationData | null;
  /** Container height in pixels. */
  readonly height?: number;
  /** Callback when any node is selected. */
  readonly onNodeSelect?: (nodeId: string) => void;
  /** Callback to re-center constellation on a different node. */
  readonly onRecenter?: (nodeId: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RegulatoryConstellationGraph({
  constellation,
  universalConstellation,
  height = 600,
  onNodeSelect,
  onRecenter,
}: RegulatoryConstellationGraphProps) {
  const cyRef = useRef<Core | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [selectedNode, setSelectedNode] = useState<RegulatoryNode | null>(null);
  const [selectedNodeSummary, setSelectedNodeSummary] = useState<string | null>(null);

  // Determine which mode we're in
  const isUniversal = !!universalConstellation;
  const data = universalConstellation || constellation;

  // ---- Initialize Cytoscape -----------------------------------------------
  useEffect(() => {
    if (!containerRef.current || cyRef.current) return undefined;

    try {
      cyRef.current = cytoscape({
        container: containerRef.current,
        style: CONSTELLATION_STYLES as any,
        layout: { name: 'preset' },
        minZoom: 0.2,
        maxZoom: 3,
        wheelSensitivity: 1,
      });

      // Node tap handler
      cyRef.current.on('tap', 'node', (event) => {
        const node = event.target as NodeSingular;
        const nodeId = node.id();
        const role = node.data('role');
        const jurisdiction = node.data('jurisdiction');

        // Center/tenant node — clear selection
        if (role === 'center' || jurisdiction === 'tenant') {
          setSelectedNode(null);
          setSelectedNodeSummary(null);
          onNodeSelect?.('');
          return;
        }

        // Organization node — show summary, offer re-center
        if (role === 'organization') {
          setSelectedNode(null);
          setSelectedNodeSummary(
            `${node.data('label')} (${node.data('role_label') || 'Organization'})`
          );
          onNodeSelect?.(nodeId);
          return;
        }

        // Regulatory node — show detail panel
        const regNode: RegulatoryNode = {
          nodeId,
          name: node.data('label'),
          jurisdiction: jurisdiction as Jurisdiction,
          edgeType: node.data('edge_type') || 'REGULATED_BY',
          reason: node.data('reason') || '',
          priority: node.data('is_primary') ? 'primary' : undefined,
          conditional: node.data('is_conditional') || false,
          notes: node.data('notes') || undefined,
        };

        setSelectedNode(regNode);
        setSelectedNodeSummary(null);
        onNodeSelect?.(nodeId);
      });

      // Double-tap → re-center on that node
      cyRef.current.on('dbltap', 'node', (event) => {
        const node = event.target as NodeSingular;
        const nodeId = node.id();
        const role = node.data('role');
        if (role !== 'center') {
          onRecenter?.(nodeId);
        }
      });

      // Background tap → deselect
      cyRef.current.on('tap', (event) => {
        if (event.target === cyRef.current) {
          setSelectedNode(null);
          setSelectedNodeSummary(null);
          onNodeSelect?.('');
        }
      });

      setIsInitialized(true);
    } catch (err) {
      console.error('Failed to initialize constellation Cytoscape:', err);
    }

    return () => {
      if (cyRef.current) {
        cyRef.current.destroy();
        cyRef.current = null;
      }
    };
  }, [onNodeSelect, onRecenter]);

  // ---- Render universal constellation -------------------------------------
  useEffect(() => {
    if (!cyRef.current || !isInitialized || !universalConstellation) return;

    try {
      const { nodes, edges } = universalConstellation;
      const cyNodes: cytoscape.ElementDefinition[] = [];
      const cyEdges: cytoscape.ElementDefinition[] = [];

      for (const node of nodes) {
        const colorKey = node.role === 'regulatory'
          ? (node.jurisdiction || 'federal')
          : node.role;

        const concentricValue = getRingValue(node.role, node.jurisdiction);

        cyNodes.push({
          data: {
            id: node.nodeId,
            label: node.name,
            role: node.role,
            jurisdiction: node.jurisdiction || node.role,
            role_label: node.metadata?.roleLabel || '',
            is_shared: (node.connectedVia?.length || 0) > 1,
            connected_via_count: node.connectedVia?.length || 0,
            concentric_value: concentricValue,
          },
          style: {
            'background-color': NODE_COLORS[colorKey] || '#228be6',
            'border-color': NODE_BORDER_COLORS[colorKey] || '#1864ab',
          },
        });
      }

      for (let i = 0; i < edges.length; i++) {
        const edge = edges[i];
        if (!edge) continue;
        const isOrgEdge = edge.edgeType !== 'REGULATED_BY' && edge.edgeType !== 'GOVERNED_BY';
        const edgeColor = isOrgEdge
          ? (ORG_EDGE_COLORS[edge.edgeType] || '#adb5bd')
          : (REG_EDGE_COLORS[
              nodes.find((n) => n.nodeId === edge.targetId)?.jurisdiction || 'federal'
            ] || '#adb5bd');

        // For GOVERNED_BY, always use gray
        const finalColor = edge.edgeType === 'GOVERNED_BY' ? '#adb5bd' : edgeColor;

        cyEdges.push({
          data: {
            id: `edge-${i}`,
            source: edge.sourceId,
            target: edge.targetId,
            edge_type: edge.edgeType,
            label: edge.label,
            layer: isOrgEdge ? 'org' : 'regulatory',
          },
          style: {
            'line-color': finalColor,
            'target-arrow-color': finalColor,
          },
        });
      }

      cyRef.current.elements().remove();
      cyRef.current.add(cyNodes);
      cyRef.current.add(cyEdges);

      cyRef.current
        .layout({
          name: 'concentric',
          concentric: (node: NodeSingular) =>
            node.data('concentric_value') || 15,
          levelWidth: () => 1,
          minNodeSpacing: 35,
          animate: true,
          animationDuration: 700,
          padding: 30,
        } as cytoscape.ConcentricLayoutOptions)
        .run();

      cyRef.current.fit(undefined, 40);
    } catch (err) {
      console.error('Failed to render universal constellation:', err);
    }
  }, [universalConstellation, isInitialized]);

  // ---- Render tenant-centric constellation (existing behavior) ------------
  useEffect(() => {
    if (!cyRef.current || !isInitialized || !constellation || isUniversal) return;

    try {
      const cyNodes: cytoscape.ElementDefinition[] = [];
      const cyEdges: cytoscape.ElementDefinition[] = [];

      // Tenant center
      cyNodes.push({
        data: {
          id: constellation.tenantId,
          label: constellation.tenantName,
          role: 'center',
          jurisdiction: 'tenant',
          concentric_value: RING_VALUES.tenant,
        },
        style: {
          'background-color': NODE_COLORS.tenant,
          'border-color': NODE_BORDER_COLORS.tenant,
        },
      });

      // Regulatory nodes
      for (const regNode of constellation.nodes) {
        const j = regNode.jurisdiction;
        cyNodes.push({
          data: {
            id: regNode.nodeId,
            label: regNode.name,
            role: 'regulatory',
            jurisdiction: j,
            edge_type: regNode.edgeType,
            reason: regNode.reason,
            is_primary: regNode.priority === 'primary',
            is_conditional: regNode.conditional || false,
            notes: regNode.notes || '',
            concentric_value: RING_VALUES[j] || 15,
          },
          style: {
            'background-color': NODE_COLORS[j],
            'border-color': NODE_BORDER_COLORS[j],
          },
        });

        const edgeColor = regNode.edgeType === 'GOVERNED_BY'
          ? '#adb5bd'
          : (REG_EDGE_COLORS[j] || '#adb5bd');

        cyEdges.push({
          data: {
            id: `edge-${regNode.nodeId}`,
            source: constellation.tenantId,
            target: regNode.nodeId,
            edge_type: regNode.edgeType,
            layer: 'regulatory',
          },
          style: {
            'line-color': edgeColor,
            'target-arrow-color': edgeColor,
          },
        });
      }

      cyRef.current.elements().remove();
      cyRef.current.add(cyNodes);
      cyRef.current.add(cyEdges);

      cyRef.current
        .layout({
          name: 'concentric',
          concentric: (node: NodeSingular) =>
            node.data('concentric_value') || 15,
          levelWidth: () => 1,
          minNodeSpacing: 40,
          animate: true,
          animationDuration: 600,
          padding: 30,
        } as cytoscape.ConcentricLayoutOptions)
        .run();

      cyRef.current.fit(undefined, 40);
    } catch (err) {
      console.error('Failed to render tenant constellation:', err);
    }
  }, [constellation, isInitialized, isUniversal]);

  // ---- Callbacks ----------------------------------------------------------
  const handleFitToView = useCallback(() => {
    cyRef.current?.fit(undefined, 40);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedNode(null);
    setSelectedNodeSummary(null);
    if (cyRef.current) cyRef.current.$(':selected').unselect();
  }, []);

  // ---- Loading / empty states ---------------------------------------------
  if (!data) {
    return (
      <Paper p="md" withBorder>
        <Stack align="center" gap="sm">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">Loading constellation...</Text>
        </Stack>
      </Paper>
    );
  }

  // ---- Header stats -------------------------------------------------------
  function renderHeaderBadges(): React.ReactNode {
    if (isUniversal && universalConstellation) {
      return (
        <>
          <Badge variant="light" color="violet" size="sm">
            {universalConstellation.center.name}
          </Badge>
          <Badge variant="light" color="blue" size="sm">
            {universalConstellation.organizationCount} orgs
          </Badge>
          <Badge variant="light" color="red" size="sm">
            {universalConstellation.uniqueRegulatoryCount} regulators
          </Badge>
          {universalConstellation.sharedRegulatoryCount > 0 && (
            <Tooltip label="Regulatory bodies shared across multiple organizations">
              <Badge variant="light" color="yellow" size="sm">
                {universalConstellation.sharedRegulatoryCount} shared
              </Badge>
            </Tooltip>
          )}
        </>
      );
    }

    if (constellation) {
      return (
        <>
          <Badge variant="light" color="blue" size="sm">
            {constellation.tenantName}
          </Badge>
          <Badge variant="light" color="red" size="sm">
            {constellation.regulatedByCount} mandatory
          </Badge>
          {constellation.governedByCount > 0 && (
            <Badge variant="light" color="gray" size="sm">
              {constellation.governedByCount} voluntary
            </Badge>
          )}
        </>
      );
    }

    return null;
  }

  // ---- Render -------------------------------------------------------------
  return (
    <Stack gap="sm">
      {/* Header */}
      <Group justify="space-between">
        <Title order={5}>
          {isUniversal ? 'Universal Constellation' : 'Regulatory Constellation'}
        </Title>
        <Group gap="xs">
          {renderHeaderBadges()}
          <Badge
            variant="light"
            color="teal"
            size="sm"
            style={{ cursor: 'pointer' }}
            onClick={handleFitToView}
          >
            Fit
          </Badge>
        </Group>
      </Group>

      {/* Legend */}
      <Group gap="xs" wrap="wrap">
        {RING_LEGEND.map((entry) => (
          <Badge key={entry.key} size="xs" variant="dot" color={entry.color}>
            {entry.label}
          </Badge>
        ))}
        <Text size="xs" c="dimmed" mx="xs">|</Text>
        {EDGE_LEGEND.map((entry) => (
          <Badge
            key={entry.key}
            size="xs"
            variant="outline"
            color={entry.color}
          >
            {entry.style === 'dashed' ? '\u2504' : '\u2500'} {entry.label}
          </Badge>
        ))}
        {isUniversal && (
          <>
            <Text size="xs" c="dimmed" mx="xs">|</Text>
            <Badge size="xs" variant="outline" color="yellow">
              Shared
            </Badge>
          </>
        )}
      </Group>

      {/* Re-center hint */}
      {isUniversal && onRecenter && (
        <Text size="xs" c="dimmed" fs="italic">
          Double-click any node to re-center the constellation on it
        </Text>
      )}

      {/* Cytoscape container */}
      <Box
        ref={containerRef}
        style={{
          width: '100%',
          height,
          border: '1px solid var(--mantine-color-gray-3)',
          borderRadius: 'var(--mantine-radius-sm)',
          background: 'var(--mantine-color-gray-0)',
        }}
      />

      {/* Selected organization summary */}
      {selectedNodeSummary && (
        <Paper p="sm" withBorder>
          <Group gap="xs">
            <Text size="sm" fw={600}>Selected:</Text>
            <Badge variant="filled" color="blue" size="sm">
              {selectedNodeSummary}
            </Badge>
            {onRecenter && (
              <Text size="xs" c="dimmed">
                Double-click to re-center
              </Text>
            )}
          </Group>
        </Paper>
      )}

      {/* Selected regulatory node detail panel */}
      {selectedNode && (
        <RegulatoryNodeDetailPanel
          node={selectedNode}
          onClose={handleCloseDetail}
        />
      )}
    </Stack>
  );
}
